import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../services/apiService';
import { phoneError, normalizePhone } from '../utils/phone';
import { DSBackLink, DSIcon, DSTextarea, DSInput, DSButton } from '../design-system/primitives';
import './Login.css';
import './ApplyMembership.css';
import DocumentSignSheet from '../components/DocumentSignSheet';

// The two documents, and the sentence a person is agreeing to for each. The
// wording differs because the commitments differ: the rules are about conduct,
// the agreement is about money.
const DOCUMENT_ROWS = [
  {
    key: 'club_rules',
    label: 'Club rules',
    acceptedKey: 'rulesAccepted',
    signatureKey: 'rulesSignature',
    agreeLabel: 'I have read the club rules and I will follow them. I understand they may change, and that I will be asked to sign again if they do.'
  },
  {
    key: 'membership_agreement',
    label: 'Membership agreement',
    acceptedKey: 'agreementAccepted',
    signatureKey: 'agreementSignature',
    agreeLabel: 'I agree to the membership agreement, including dues of $50 a month.'
  }
];

function ApplyMembership() {
  const [formData, setFormData] = useState({
    phoneNumber: '',
    reasonForJoining: '',
    visitedBefore: null,
    visitedBeforeDetails: '',
    knowsMember: null,
    knowsMemberName: '',
    contributionAreas: [],
    contributionOther: '',
    availability: [],
    preferredTime: '',
    agreementAccepted: false,
    rulesAccepted: false,
    rulesSignature: '',
    agreementSignature: ''
  });
  // Which document's sheet is open, if any.
  const [signingDoc, setSigningDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [agreementContent, setAgreementContent] = useState(null);
  const [agreementLoading, setAgreementLoading] = useState(true);
  const [rulesContent, setRulesContent] = useState(null);
  const [rulesLoading, setRulesLoading] = useState(true);
  const errorRef = useRef(null);

  const bothSigned = formData.rulesAccepted && formData.agreementAccepted;

  useEffect(() => {
    if (error) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  useEffect(() => {
    // Load current user data and pre-populate form
    const loadUserData = async () => {
      try {
        const response = await apiService.getCurrentUser();
        const user = response.data;
        setCurrentUser(user);
        
        // Pre-populate form with existing user data
        setFormData(prev => ({
          ...prev,
          phoneNumber: user.phone_number || ''
        }));
      } catch (err) {
        console.error('Error loading user data:', err);
        setError('Failed to load user data. Please try refreshing the page.');
      }
    };
    
    // Load membership agreement content from WordPress
    const loadAgreementContent = async () => {
      try {
        const response = await apiService.getWordPressPage('capital-pinball-membership-agreement');
        setAgreementContent(response.data);
      } catch (err) {
        console.error('Error loading membership agreement:', err);
        // Fallback to hardcoded content on error
        setAgreementContent({
          title: 'Membership Agreement',
          content: '<p>Unable to load agreement content. Please contact an administrator.</p>'
        });
      } finally {
        setAgreementLoading(false);
      }
    };

    // Load club rules content from WordPress
    const loadRulesContent = async () => {
      try {
        const response = await apiService.getClubRules();
        setRulesContent(response.data);
      } catch (err) {
        console.error('Error loading club rules:', err);
        // Fallback to hardcoded content on error
        setRulesContent({
          version: 'N/A',
          content: '<p>Unable to load club rules. Please contact an administrator.</p>'
        });
      } finally {
        setRulesLoading(false);
      }
    };
    
    loadUserData();
    loadAgreementContent();
    loadRulesContent();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'agreementAccepted' || name === 'rulesAccepted') {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      } else if (name === 'contributionAreas') {
        setFormData(prev => ({
          ...prev,
          contributionAreas: checked
            ? [...prev.contributionAreas, value]
            : prev.contributionAreas.filter(area => area !== value)
        }));
      } else if (name === 'availability') {
        setFormData(prev => ({
          ...prev,
          availability: checked
            ? [...prev.availability, value]
            : prev.availability.filter(slot => slot !== value)
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleVisitedBeforeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      visitedBefore: value,
      visitedBeforeDetails: value ? prev.visitedBeforeDetails : ''
    }));
  };

  const handleKnowsMemberChange = (value) => {
    setFormData(prev => ({
      ...prev,
      knowsMember: value,
      knowsMemberName: value ? prev.knowsMemberName : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const phoneMessage = phoneError(formData.phoneNumber);
    if (phoneMessage) {
      setError(phoneMessage);
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.submitApplication({
        ...formData,
        phoneNumber: normalizePhone(formData.phoneNumber)
      });
      
      // Redirect to onboarding dashboard
      if (response.data.redirectTo) {
        window.location.href = response.data.redirectTo;
      } else {
        // Fallback if redirectTo not provided
        window.location.href = '/my-membership';
      }
    } catch (err) {
      const data = err.response?.data;
      const validationMessage = data?.errors?.map(e => e.msg).filter(Boolean).join(' ');
      setError(validationMessage || data?.error || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ marginBottom: '20px' }}>
          <DSBackLink tone="subtle" size="sm" onClick={() => window.history.back()}>Back</DSBackLink>
        </div>

        <h1>Apply for Membership</h1>
        
        <p style={{ marginBottom: '20px', fontSize: '14px', color: 'var(--ds-color-textMuted)' }}>
          Complete your membership application to gain full access to our facilities.
          {currentUser?.membership_status === 'pending' && (
            <span style={{ display: 'block', marginTop: '10px', color: 'var(--ds-color-statusWarningText)', fontWeight: 'bold' }}>
              <DSIcon name="warning" /> You already have a pending application. Submitting again will update your information.
            </span>
          )}
        </p>

        {error && <div ref={errorRef} className="alert alert-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <DSInput label="Phone Number *" helpText="Required for emergency contact purposes"
              id="phoneNumber"
              name="phoneNumber"
              type="tel"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
              disabled={loading}
              inputMode="tel"
              maxLength={20}
              placeholder="(555) 123-4567" />

          <DSTextarea label="Why are you interested in joining Capital Pinball? *"
              id="reasonForJoining"
              name="reasonForJoining"
              value={formData.reasonForJoining}
              onChange={handleChange}
              required
              disabled={loading}
              rows="4"
              style={{ 
                width: '100%', 
                padding: '10px', 
                border: '1px solid #ccc', 
                borderRadius: '4px',
                fontFamily: 'inherit',
                fontSize: '14px'
              }}
              placeholder="Tell us what interests you about becoming a member..." />

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              Have you been to the club before? *
            </label>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="radio"
                  name="visitedBefore"
                  checked={formData.visitedBefore === true}
                  onChange={() => handleVisitedBeforeChange(true)}
                  required
                  disabled={loading}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="radio"
                  name="visitedBefore"
                  checked={formData.visitedBefore === false}
                  onChange={() => handleVisitedBeforeChange(false)}
                  required
                  disabled={loading}
                />
                No
              </label>
            </div>

            {formData.visitedBefore === true && (
              <DSTextarea
                name="visitedBeforeDetails"
                value={formData.visitedBeforeDetails}
                onChange={handleChange}
                disabled={loading}
                rows="3"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  fontSize: '14px'
                }}
                placeholder="Tell us about when you've been in - what event or visit brought you by, and what you remember about it..."
              />
            )}

            {formData.visitedBefore === false && (
              <p style={{
                fontSize: '13px',
                color: 'var(--ds-color-textMuted)',
                marginTop: '10px',
                lineHeight: '1.5',
                backgroundColor: 'var(--ds-color-textDefault)',
                padding: '12px',
                borderRadius: '4px'
              }}>
                That's totally okay! We ask that prospective members visit during public hours or for an event to get to know us and the space before becoming a member. We are open Tuesdays, Wednesdays and Fridays from 6-10pm for you to check out the space. Please still submit your application and we will still reach out to you with next steps.
              </p>
            )}
          </div>

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              Do you know anyone who is a member of the club? *
            </label>
            <p style={{ fontSize: '13px', color: 'var(--ds-color-textMuted)', marginBottom: '10px' }}>
              This serves as a reference for your application.
            </p>
            <div style={{ display: 'flex', gap: '20px', marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="radio"
                  name="knowsMember"
                  checked={formData.knowsMember === true}
                  onChange={() => handleKnowsMemberChange(true)}
                  required
                  disabled={loading}
                />
                Yes
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="radio"
                  name="knowsMember"
                  checked={formData.knowsMember === false}
                  onChange={() => handleKnowsMemberChange(false)}
                  required
                  disabled={loading}
                />
                No
              </label>
            </div>

            {formData.knowsMember === true && (
              <DSInput
                type="text"
                name="knowsMemberName"
                value={formData.knowsMemberName}
                onChange={handleChange}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  fontSize: '14px'
                }}
                placeholder="Name of the member you know"
              />
            )}
          </div>

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              Community Contribution
            </label>
            <p style={{ fontSize: '14px', color: 'var(--ds-color-textMuted)', marginBottom: '15px', lineHeight: '1.5' }}>
              Capital Pinball thrives through member involvement. Please indicate any ways you may be able to help or contribute:
            </p>
            
            <div style={{ marginLeft: '10px' }}>
              {[
                'Hosting or assisting with events or tournaments',
                'Machine repair or technical skills',
                'Providing pinball machines or arcade games',
                'Cleaning, organizing, or general maintenance',
                'Outreach, promotion, or sponsorship assistance'
              ].map((area) => (
                <label key={area} style={{ display: 'block', marginBottom: '8px', fontSize: '14px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="contributionAreas"
                    value={area}
                    checked={formData.contributionAreas.includes(area)}
                    onChange={handleChange}
                    disabled={loading}
                    style={{ marginRight: '8px' }}
                  />
                  {area}
                </label>
              ))}
              
              <label style={{ display: 'block', marginTop: '12px', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  name="contributionAreas"
                  value="Other"
                  checked={formData.contributionAreas.includes('Other')}
                  onChange={handleChange}
                  disabled={loading}
                  style={{ marginRight: '8px' }}
                />
                Other (please describe below)
              </label>
              
              {formData.contributionAreas.includes('Other') && (
                <DSTextarea
                  name="contributionOther"
                  value={formData.contributionOther}
                  onChange={handleChange}
                  disabled={loading}
                  rows="2"
                  style={{ 
                    width: '100%', 
                    padding: '10px', 
                    border: '1px solid #ccc', 
                    borderRadius: '4px',
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    marginTop: '10px'
                  }}
                  placeholder="Please describe other ways you'd like to contribute..."
                />
              )}
            </div>
          </div>

          {/* Orientation Availability Section */}
          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
              General Availability for Orientation *
            </label>
            <p style={{ fontSize: '13px', color: 'var(--ds-color-textMuted)', marginBottom: '15px' }}>
              Select all time slots when you're generally available for an in-person orientation session
            </p>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'auto 1fr 1fr',
              gap: '8px',
              alignItems: 'center',
              marginBottom: '15px'
            }}>
              <div></div>
              <div style={{ fontWeight: 'bold', fontSize: '13px', textAlign: 'center' }}>AM</div>
              <div style={{ fontWeight: 'bold', fontSize: '13px', textAlign: 'center' }}>PM</div>
              
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                <React.Fragment key={day}>
                  <div style={{ fontSize: '14px' }}>{day}</div>
                  <div style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      name="availability"
                      value={`${day}-AM`}
                      checked={formData.availability.includes(`${day}-AM`)}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      name="availability"
                      value={`${day}-PM`}
                      checked={formData.availability.includes(`${day}-PM`)}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                </React.Fragment>
              ))}
            </div>

            <label htmlFor="preferredTime" style={{ display: 'block', marginTop: '15px', fontWeight: 'bold' }}>
              Preferred Time Details
            </label>
            <DSTextarea
              id="preferredTime"
              name="preferredTime"
              value={formData.preferredTime}
              onChange={handleChange}
              disabled={loading}
              rows="3"
              style={{ 
                width: '100%', 
                padding: '10px', 
                border: '1px solid #ccc', 
                borderRadius: '4px',
                fontFamily: 'inherit',
                fontSize: '14px',
                marginTop: '5px'
              }}
              placeholder="e.g., I prefer weekday evenings after 6pm, or Saturday mornings..."
            />
          </div>

          {/* The two documents, as rows you open, read and sign.
              Same shape as re-signing later, and the same rules about what a
              signature is: read to the end, tick the box, type your own name.
              Previously these were two long scroll boxes inline in the form
              with a checkbox under each, which made agreeing the easiest thing
              on the page to do without reading. */}
          <div className="apply-docs">
            <p className="apply-docs__head">Documents to sign</p>
            {DOCUMENT_ROWS.map((row) => {
              const signed = formData[row.acceptedKey];
              const content = row.key === 'club_rules' ? rulesContent : agreementContent;
              const isLoading = row.key === 'club_rules' ? rulesLoading : agreementLoading;
              return (
                <button
                  type="button"
                  key={row.key}
                  className="apply-doc-row"
                  disabled={isLoading || !content}
                  onClick={() => setSigningDoc(row.key)}
                >
                  <span className="apply-doc-row__main">
                    <span className="apply-doc-row__title">{row.label}</span>
                    <span className="apply-doc-row__meta">
                      {isLoading
                        ? 'Loading...'
                        : signed
                          ? `Signed as ${formData[row.signatureKey]}`
                          : 'Not signed yet'}
                    </span>
                  </span>
                  <span className={`apply-doc-row__state ${signed ? 'apply-doc-row__state--done' : ''}`}>
                    {signed ? 'Signed' : 'Read and sign'}
                  </span>
                </button>
              );
            })}
            <p className="apply-doc-note">
              Your signature is kept with the date you signed. If the club changes
              something material later, we will show you what changed and ask you
              to sign again.
            </p>
          </div>


          <DSButton tone="primary" appearance="solid" className="login-button" type="submit" disabled={loading || !bothSigned} style={{ opacity: (!bothSigned && !loading) ? 0.6 : 1 }}>
            {loading ? 'Submitting...' : 'Submit Application'}
          </DSButton>
        </form>
      </div>

      {signingDoc && (() => {
        const row = DOCUMENT_ROWS.find((d) => d.key === signingDoc);
        const content = signingDoc === 'club_rules' ? rulesContent : agreementContent;
        const expectedName = [currentUser?.first_name, currentUser?.last_name]
          .filter(Boolean).join(' ').trim();
        return (
          <DocumentSignSheet
            title={row.label}
            label={row.label}
            content={content?.content || ''}
            agreeLabel={row.agreeLabel}
            expectedName={expectedName}
            currentUser={currentUser}
            onClose={() => setSigningDoc(null)}
            onSign={(signature) => {
              // Recorded in form state and written server-side when the
              // application is submitted, so a half-finished application does
              // not leave a signature behind for a document nobody joined for.
              setFormData((prev) => ({
                ...prev,
                [row.acceptedKey]: true,
                [row.signatureKey]: signature
              }));
              setSigningDoc(null);
            }}
          />
        );
      })()}
    </div>
  );
}

export default ApplyMembership;
